import { useEffect, useState } from 'react';
import { useGlobalStore } from '@digiwedge/global-state';
import { readEnvVar } from '../../env.runtime.js';

type Check = { feature: string; action: string; tenantId?: string };

export type CapabilityReason = 'explicit' | 'bypass' | 'none' | undefined;
export type CapabilitiesResult = {
  loading: boolean;
  canTenantRead: boolean;
  canTenantCreate: boolean;
  canUserRead: boolean;
  canRoleRead: boolean;
  canPermissionRead: boolean;
  canPermissionAssignmentRead: boolean;
  canFeatureRead: boolean;
  canFeatureFlagRead: boolean;
  canConsentRead: boolean;
  canSessionRead: boolean;
  canLoginAttemptsRead: boolean;
  canInvitationRead: boolean;
  reasons?: Record<string, CapabilityReason>; // key: `${feature}:${action}`
};

export function useCapabilities(): CapabilitiesResult {
  const [state, setState] = useState<CapabilitiesResult>({
    loading: true,
    canTenantRead: true, // optimistic until checked
    canTenantCreate: true,
    canUserRead: true,
    canRoleRead: true,
    canPermissionRead: true,
    canPermissionAssignmentRead: true,
    canFeatureRead: true,
    canFeatureFlagRead: true,
    canConsentRead: true,
    canSessionRead: true,
    canLoginAttemptsRead: true,
    canInvitationRead: true,
  });

  const token = useGlobalStore((s) => s.token);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        // Wait until we have a token; without it the API will 401 and pollute logs
        if (!token) {
          setState((s) => ({ ...s, loading: false }));
          return;
        }
        const checks: Check[] = [
          { feature: 'TENANT_MGMT', action: 'read' },
          { feature: 'TENANT_MGMT', action: 'create' },
          { feature: 'USER_MGMT', action: 'read' },
          { feature: 'ROLE_MGMT', action: 'read' },
          { feature: 'PERMISSION_MGMT', action: 'read' },
          { feature: 'PERMISSION_ASSIGNMENT', action: 'read' },
          { feature: 'FEATURE_MGMT', action: 'read' },
          { feature: 'FEATURE_FLAG', action: 'read' },
          { feature: 'CONSENT_MGMT', action: 'read' },
          { feature: 'SESSION_MGMT', action: 'read' },
          { feature: 'LOGIN_ATTEMPTS', action: 'read' },
          { feature: 'INVITATION_MGMT', action: 'read' },
        ];
        const base = readEnvVar('VITE_ACCESS_CONTROL_BASE_URL') ?? '';
        const url = `${String(base).replace(/\/$/, '') || ''}/api/capabilities/can`;
        const headers: Record<string, string> = { 'content-type': 'application/json' };
        headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(url.startsWith('/api/') ? url : url.replace(/^\//, ''), {
          method: 'POST',
          headers,
          body: JSON.stringify({ checks }),
        });
        if (!res.ok) throw new Error(`capabilities/can HTTP ${res.status}`);
        const data = (await res.json()) as {
          results: Array<{
            feature: string;
            action: string;
            allowed: boolean;
            reason?: 'explicit' | 'bypass' | 'none';
          }>;
        };
        const get = (f: string, a: string) => {
          const m = data.results.find((r) => r.feature === f && r.action === a);
          return m?.allowed ?? false;
        };
        const reasons: Record<string, CapabilityReason> = {};
        for (const r of data.results) reasons[`${r.feature}:${r.action}`] = r.reason ?? undefined;
        if (!cancelled) {
          setState({
            loading: false,
            canTenantRead: get('TENANT_MGMT', 'read'),
            canTenantCreate: get('TENANT_MGMT', 'create'),
            canUserRead: get('USER_MGMT', 'read'),
            canRoleRead: get('ROLE_MGMT', 'read'),
            canPermissionRead: get('PERMISSION_MGMT', 'read'),
            canPermissionAssignmentRead: get('PERMISSION_ASSIGNMENT', 'read'),
            canFeatureRead: get('FEATURE_MGMT', 'read'),
            canFeatureFlagRead: get('FEATURE_FLAG', 'read'),
            canConsentRead: get('CONSENT_MGMT', 'read'),
            canSessionRead: get('SESSION_MGMT', 'read'),
            canLoginAttemptsRead: get('LOGIN_ATTEMPTS', 'read'),
            canInvitationRead: get('INVITATION_MGMT', 'read'),
            reasons,
          });
        }
      } catch {
        if (!cancelled) setState((s) => ({ ...s, loading: false }));
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return state;
}
